import { Box, Typography } from '@mui/material';
import React, { memo } from 'react';
import {
  findFailedRefunds,
  findSuccessRefunds,
  formatDisplayNumber,
  getFirstLine,
  getSecondLine,
  getOrderTypeLabel,
  formatDateWithTimeZone,
  ORDER_TYPE_LABEL,
  defaultProvider,
} from "src/utils";
import PhoneLink from "../PhoneLink/PhoneLink";

const RefundMessage = ({ failedRefunds = [], successRefunds = [] }) => {
  if (failedRefunds.length > 0 && successRefunds.length === 0) {
    return <Typography className="displayRefund">Refund Declined</Typography>;
  }
  if (successRefunds.length > 0) {
    return <Typography className="displayRefund">Refund</Typography>;
  }
  return null;
};

const ProviderDeliveryOrder = ({ provider }) => {
  return (
    <>
      <Box className="orderType uppercase">
        {`Delivery By ${provider}`}
      </Box>
      <hr className="line-header" />
    </>
  )
}

const OrderInfo = ({ createdAt, number, timeZoneId, orderType, beReadyTime }) => {
  return (
    <>
      <Box className="order-number">
        {createdAt ? (
          <>
            Ordered: {formatDateWithTimeZone(createdAt, timeZoneId)} <br />
            {beReadyTime && (
              <>
                Ready At: {formatDateWithTimeZone(beReadyTime, timeZoneId)} <br />
              </>
            )}
            {`Order Number #${number}`}
          </>
        ) : (
          <>
            {beReadyTime && (
              <>
                  Ready At: {formatDateWithTimeZone(beReadyTime)} <br />
              </>
            )}
            {`Order Number #${number}`}
          </>
        )}
      </Box>

      {orderType && (
        <>
          <hr className="line-header-long"/>
          <Typography className="orderType">
            {getOrderTypeLabel(orderType)}
          </Typography>
        </>
      )}
      <hr className="line-header-long"/>
    </>
  )
}

const BusinessInfo = ({ business }) => {
  if (!business) return null;

  const { title, companyName, image = null, addressInfo, phone = null, supportPhone = null } = business;

  return (
    <>
      {image?.url && (
        <Box className="business-logo">
          <img src={image?.url} alt="business logo" />
        </Box>
      )}
      <Typography className="companyName">{title || companyName}</Typography>
      {addressInfo && (
        <Typography className="address_info">
          {getFirstLine(addressInfo?.address1, addressInfo?.address2)} <br />
          {getSecondLine(addressInfo?.city, addressInfo?.state, addressInfo?.zipCode)}
        </Typography>
      )}
      {phone && (
        <Typography className="phone-number">
          <PhoneLink phone={supportPhone || phone} />
        </Typography>
      )}
      <hr className="line-header" />
    </>
  );
};

const CashierInfo = ({ employee = null, displayNumber, register = null, tableName = null }) => {
  return (
    <>
      {register && (
        <Box className="cashierInfo">
          <Typography className="uppercase">Register:&nbsp;&nbsp;&nbsp;
            <span>{register}</span></Typography>
        </Box>
      )}
      {employee && (
        <Box className="cashierInfo">
          <Typography className="uppercase">Employee:&nbsp;
            <span>{employee}</span></Typography>
        </Box>
      )}
      {tableName && (
        <Box className="cashierInfo">
          <Typography className="uppercase">Table:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 
            <span>{tableName}</span></Typography>
        </Box>
      )}
      {displayNumber && (
        <Box className="cashierInfo">
          <Typography className="uppercase">Check #:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <span>{formatDisplayNumber(displayNumber)}</span></Typography>
        </Box>
      )}
    </>
  )
}

const OrderHeader = ({ orderData, timeZoneId }) => {
  if (!orderData) return null;

  const {
    displayNumber,
    metadata,
    type = null,
    provider = null,
    register = null
  } = orderData;
  const business = metadata?.business;
  const transactions = metadata?.transactions || [];
  const failedRefunds = findFailedRefunds(transactions);
  const successRefunds = findSuccessRefunds(transactions);
  const employee = metadata?.tableDetails?.tableDetails?.employeeInfo?.initials ||
    metadata?.employeeInitials || '';

  return (
    <Box id="receiptHeader">
      <Box id="receiptHeaderInfo">
        {displayNumber && (
          <Typography className="displayNumber">{formatDisplayNumber(displayNumber)}</Typography>
        )}
        <RefundMessage failedRefunds={failedRefunds} successRefunds={successRefunds} />
        <BusinessInfo business={business} />
        <OrderInfo
          createdAt={orderData?.placedAt}
          number={orderData?.orderNumber}
          timeZoneId={timeZoneId}
          orderType={orderData?.orderType}
          beReadyTime={orderData?.beReadyTime}
        />
        {type === ORDER_TYPE_LABEL.delivery && provider !== defaultProvider ? (
          <ProviderDeliveryOrder provider={provider} />
        ) : null}
        <CashierInfo
          employee={employee}
          displayNumber={displayNumber}
          register={orderData?.metadata?.deviceInfo?.title}
          tableName={
            orderData?.metadata?.tableDetails?.tableDetails?.name ||
             orderData?.metadata?.tableNumber || ''
          }
        />
        <hr className="line-header" />
      </Box>
    </Box>
  );
};

export default memo(OrderHeader);
